import { getAnnualMaskFileSortTime } from "./annualMaskClinicalDataUtils";

export const ANNUAL_MASK_BATCHES_COLLECTION = "annualMaskPurchaseBatches";
export const ANNUAL_MASK_CASES_COLLECTION = "annualMaskCases";
export const ANNUAL_MASK_ACCESS_COLLECTION = "annualMaskAccessEmails";

export const ANNUAL_MASK_STATUS = {
  DRAFT: "Draft",
  DOCTOR_STEP_SKIPPED: "Doctor Step Skipped — Awaiting Signed Certificate",
  EMAIL_SENT_TO_DOCTOR: "Emailed to Doctor",
  SIGNED_CERTIFICATE_UPLOADED: "Signed Medical Certificate Uploaded",
  SIGNED_CERTIFICATE_SKIPPED: "Signed Certificate Upload Skipped",
  SOCIAL_SECURITY_FILE_CHECKED: "File Checked for Social Security",
  SOCIAL_SECURITY_SUBMITTED: "Sent to Social Security",
  SOCIAL_SECURITY_NOT_PASSED: "Not Passed by Social Security",
  SOCIAL_SECURITY_APPROVED: "Passed by Social Security",
  READY_TO_PURCHASE: "Ready to Purchase",
  ORDER_CREATED: "Order Created",
  ORDER_COMPLETED: "Order Completed",
};

export const ANNUAL_MASK_STATUS_OPTIONS = [
  {
    value: ANNUAL_MASK_STATUS.DRAFT,
    label: "Draft",
    badge: "secondary",
  },
  {
    value: ANNUAL_MASK_STATUS.DOCTOR_STEP_SKIPPED,
    label: "Doctor Step Skipped — Awaiting Signed Certificate",
    badge: "info",
  },
  {
    value: ANNUAL_MASK_STATUS.EMAIL_SENT_TO_DOCTOR,
    label: "Emailed to Doctor",
    badge: "info",
  },
  {
    value: ANNUAL_MASK_STATUS.SIGNED_CERTIFICATE_UPLOADED,
    label: "Signed Medical Certificate Uploaded",
    badge: "primary",
  },
  {
    value: ANNUAL_MASK_STATUS.SIGNED_CERTIFICATE_SKIPPED,
    label: "Signed Certificate Upload Skipped",
    badge: "warning",
  },
  {
    value: ANNUAL_MASK_STATUS.SOCIAL_SECURITY_FILE_CHECKED,
    label: "Waiting for Social Security Result",
    badge: "warning",
  },
  {
    value: ANNUAL_MASK_STATUS.SOCIAL_SECURITY_SUBMITTED,
    label: "Waiting for Social Security Result",
    badge: "warning",
  },
  {
    value: ANNUAL_MASK_STATUS.SOCIAL_SECURITY_NOT_PASSED,
    label: "Not Passed by Social Security",
    badge: "danger",
  },
  {
    value: ANNUAL_MASK_STATUS.SOCIAL_SECURITY_APPROVED,
    label: "Passed by Social Security",
    badge: "success",
  },
  {
    value: ANNUAL_MASK_STATUS.READY_TO_PURCHASE,
    label: "Ready to Purchase",
    badge: "success",
  },
  {
    value: ANNUAL_MASK_STATUS.ORDER_CREATED,
    label: "Order Created",
    badge: "primary",
  },
  {
    value: ANNUAL_MASK_STATUS.ORDER_COMPLETED,
    label: "Order Completed",
    badge: "success",
  },
];

export const ANNUAL_MASK_STATUS_SEQUENCE = ANNUAL_MASK_STATUS_OPTIONS.map(
  (item) => item.value,
);

export const ANNUAL_MASK_SOCIAL_SECURITY_DECISION = {
  PENDING: "pending",
  PASSED: "passed",
  NOT_PASSED: "not_passed",
};

export const getAnnualMaskSocialSecurityDecision = (caseData = {}) => {
  if (
    caseData.socialSecurityDecision ===
      ANNUAL_MASK_SOCIAL_SECURITY_DECISION.PASSED ||
    caseData.socialSecurityApproved ||
    [
      ANNUAL_MASK_STATUS.SOCIAL_SECURITY_APPROVED,
      ANNUAL_MASK_STATUS.READY_TO_PURCHASE,
      ANNUAL_MASK_STATUS.ORDER_CREATED,
      ANNUAL_MASK_STATUS.ORDER_COMPLETED,
    ].includes(caseData.status)
  ) {
    return ANNUAL_MASK_SOCIAL_SECURITY_DECISION.PASSED;
  }

  if (
    caseData.socialSecurityDecision ===
      ANNUAL_MASK_SOCIAL_SECURITY_DECISION.NOT_PASSED ||
    caseData.status === ANNUAL_MASK_STATUS.SOCIAL_SECURITY_NOT_PASSED
  ) {
    return ANNUAL_MASK_SOCIAL_SECURITY_DECISION.NOT_PASSED;
  }

  return ANNUAL_MASK_SOCIAL_SECURITY_DECISION.PENDING;
};

export const normalizeAnnualMaskAccessEmail = (value) =>
  String(value || "").trim().toLocaleLowerCase();

export const getAnnualMaskSelectedSaleId = (caseData = {}) =>
  String(
    (caseData.selectedSale &&
      (caseData.selectedSale.saleId || caseData.selectedSale.id)) ||
      caseData.saleId ||
      "",
  ).trim();

export const getAnnualMaskSelectedSaleName = (caseData = {}) =>
  String(
    (caseData.selectedSale && caseData.selectedSale.name) ||
      caseData.saleName ||
      "",
  ).trim();

export const getAnnualMaskDefaultBatchName = (followup = {}, usageReport = null, now = new Date()) => {
  let date = now;
  if (usageReport && !usageReport.pendingUpload && !usageReport.localFile) {
    const uploadedAt = usageReport.uploadedAt;
    const dayFirst = typeof uploadedAt === "string" && uploadedAt.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
    const timestamp = dayFirst
      ? new Date(`${dayFirst[3]}-${dayFirst[2]}-${dayFirst[1]}T12:00:00+07:00`).getTime()
      : getAnnualMaskFileSortTime({ ...usageReport, queuedAt: null, localImportedAt: null });
    if (timestamp && Number.isFinite(timestamp)) date = new Date(timestamp);
  }
  const parts = new Intl.DateTimeFormat("en-GB", { timeZone: "Asia/Bangkok", year: "numeric", month: "2-digit" }).formatToParts(date);
  const month = `${parts.find((part) => part.type === "year").value}-${parts.find((part) => part.type === "month").value}`;
  const patientName = [
    followup.firstnameTh || followup.firstname,
    followup.lastnameTh || followup.lastname,
  ]
    .map((value) => String(value || "").trim())
    .filter(Boolean)
    .join(" ");
  const patientLabel = patientName || String(followup.hn || "").trim();

  return `Annual Mask ${month}${patientLabel ? ` - ${patientLabel}` : ""}`;
};

export const getAnnualMaskNextBatchName = (
  followup = {},
  existingCases = [],
  usageReport = null,
  now = new Date(),
) => {
  const defaultName = getAnnualMaskDefaultBatchName(followup, usageReport, now);
  const existingNames = new Set(
    existingCases
      .map((caseItem) =>
        String((caseItem && caseItem.batchName) || "").trim(),
      )
      .filter(Boolean),
  );

  if (!existingNames.has(defaultName)) return defaultName;

  let sequence = 2;
  while (existingNames.has(`${defaultName} #${sequence}`)) {
    sequence += 1;
  }

  return `${defaultName} #${sequence}`;
};

const normalizeAnnualMaskDoctorValue = (value) =>
  String(value || "").trim().toLocaleLowerCase();

export const findAnnualMaskDoctorForFollowup = (
  doctors = [],
  doctorConsult,
) => {
  const selectedDoctor = normalizeAnnualMaskDoctorValue(doctorConsult);
  if (!selectedDoctor || selectedDoctor === "กรุณาเลือก") return null;

  return (
    doctors.find((doctor) =>
      [
        doctor && doctor.id,
        doctor && doctor.name,
        doctor && doctor.doctorName,
        doctor && doctor.doctorId,
        doctor && doctor.email,
      ].some(
        (value) => normalizeAnnualMaskDoctorValue(value) === selectedDoctor,
      ),
    ) || null
  );
};

export const getAnnualMaskStatusConfig = (status) =>
  ANNUAL_MASK_STATUS_OPTIONS.find((item) => item.value === status) ||
  ANNUAL_MASK_STATUS_OPTIONS[0];

export const isAnnualMaskDraftEditable = (caseData = {}) =>
  (!caseData.status || [ANNUAL_MASK_STATUS.DRAFT, ANNUAL_MASK_STATUS.DOCTOR_STEP_SKIPPED].includes(caseData.status)) &&
  !caseData.doctorEmailSent;

export const canSkipAnnualMaskDoctorStep = (caseData = {}) =>
  (!caseData.status || caseData.status === ANNUAL_MASK_STATUS.DRAFT) &&
  !caseData.doctorEmailSent &&
  !caseData.signedMedicalCertificate &&
  !caseData.saleOrderId &&
  getAnnualMaskSocialSecurityDecision(caseData) === ANNUAL_MASK_SOCIAL_SECURITY_DECISION.PENDING;

export const getAnnualMaskDoctorSkipUnavailableReason = (caseData = {}) => {
  if (canSkipAnnualMaskDoctorStep(caseData)) return "";
  if (caseData.doctorEmailSent) return "The doctor email has already been sent.";
  if (caseData.status === ANNUAL_MASK_STATUS.DOCTOR_STEP_SKIPPED) return "This step has already been skipped.";
  if (caseData.saleOrderId || [ANNUAL_MASK_STATUS.ORDER_CREATED, ANNUAL_MASK_STATUS.ORDER_COMPLETED].includes(caseData.status)) return "A Sale Order already exists; this step is locked.";
  if (caseData.signedMedicalCertificate) return "The signed certificate is already uploaded; continue with the next step.";
  return "The case has moved beyond Draft; continue with the current step.";
};

export const isAnnualMaskSignedCertificateStepComplete = (caseData = {}) =>
  Boolean(caseData.signedMedicalCertificate ||
    (caseData.signedCertificateUploadSkip && caseData.signedCertificateUploadSkip.confirmed === true));

export const canSkipAnnualMaskSignedCertificate = (caseData = {}) =>
  !isAnnualMaskSignedCertificateStepComplete(caseData) &&
  !caseData.saleOrderId &&
  getAnnualMaskSocialSecurityDecision(caseData) === ANNUAL_MASK_SOCIAL_SECURITY_DECISION.PENDING &&
  (!caseData.status || [
    ANNUAL_MASK_STATUS.DRAFT,
    ANNUAL_MASK_STATUS.DOCTOR_STEP_SKIPPED,
    ANNUAL_MASK_STATUS.EMAIL_SENT_TO_DOCTOR,
  ].includes(caseData.status));

// Generating a replacement form or uploading a replacement signed file should
// never move an already-approved/order-complete case backwards in the workflow.
export const advanceAnnualMaskStatus = (currentStatus, nextStatus) => {
  const currentIndex = ANNUAL_MASK_STATUS_SEQUENCE.indexOf(currentStatus);
  const nextIndex = ANNUAL_MASK_STATUS_SEQUENCE.indexOf(nextStatus);

  if (currentIndex === -1 || nextIndex === -1 || currentIndex < nextIndex) {
    return nextStatus;
  }

  return currentStatus;
};

export const getAnnualMaskCaseStatusForSaleOrder = (saleOrderStatus) => {
  if (saleOrderStatus === "เสร็จสิ้น") {
    return ANNUAL_MASK_STATUS.ORDER_COMPLETED;
  }

  return ANNUAL_MASK_STATUS.ORDER_CREATED;
};

export const getAnnualMaskRoleList = (currentUser) =>
  currentUser && Array.isArray(currentUser.roles) ? currentUser.roles : [];

export const canManageAnnualMaskPurchase = (currentUser) =>
  getAnnualMaskRoleList(currentUser).some((role) =>
    ["super_admin", "admin"].includes(role),
  );

export const canAdministerAnnualMaskPurchase = (currentUser) =>
  getAnnualMaskRoleList(currentUser).some((role) =>
    ["super_admin", "admin"].includes(role),
  );

export const isAnnualMaskSuperAdmin = (currentUser) =>
  getAnnualMaskRoleList(currentUser).includes("super_admin");

export const getAnnualMaskCasePath = (batchId, caseId) =>
  `${ANNUAL_MASK_BATCHES_COLLECTION}/${batchId}/${ANNUAL_MASK_CASES_COLLECTION}/${caseId}`;

export const isAnnualMaskOrderComplete = (caseData = {}) =>
  caseData.status === ANNUAL_MASK_STATUS.ORDER_COMPLETED ||
  caseData.saleOrderStatus === "เสร็จสิ้น";

// Store a calendar date without a timezone so the patient's planned day stays stable.
export const isAnnualMaskReceiptDateValid = (value) => {
  if (typeof value !== "string" || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const date = new Date(`${value}T00:00:00.000Z`);
  return !Number.isNaN(date.getTime()) && date.toISOString().slice(0, 10) === value;
};

// Use the real available Firebase sales record; never manufacture a sales ID.
export const withAnnualMaskDefaultSale = (caseData, sales = []) => {
  if (!caseData || caseData.saleOrderId ||
    [ANNUAL_MASK_STATUS.ORDER_CREATED, ANNUAL_MASK_STATUS.ORDER_COMPLETED].includes(caseData.status) ||
    getAnnualMaskSelectedSaleId(caseData) || getAnnualMaskSelectedSaleName(caseData) ||
    getAnnualMaskSocialSecurityDecision(caseData) !== ANNUAL_MASK_SOCIAL_SECURITY_DECISION.PASSED) return caseData;
  const center = sales.find((sale) => String(sale.name || "").trim().toLowerCase() === "center");
  return center ? { ...caseData, selectedSale: { saleId: center.id, name: String(center.name).trim() } } : caseData;
};
